import { getTurnsByDateRange } from "./TurnRateHelpers";
import calculateMaxTurn from "@/utils/CalculateMaxTurn";

const filterTurnData = (turnData, filterOptions, filters) => {
	if (!filterOptions || filterOptions.length === 0) {
		return turnData;
	}

	// Combine all the relevant filters into a single function,
	const combinedFilter = filterOptions
		.map((i) => (i < filters.length ? filters[i] : () => true))
		.reduce(
			(accumulator, filterFn) => (t) => accumulator(t) || filterFn(t),
			() => false
		);

	return turnData.filter(combinedFilter);
};

const getMinMaxPlotValues = (selectedData, valueRanges, defaultMin, defaultMax) => {
	const result = { min: defaultMin, max: defaultMax };
	if (selectedData.length > 0) {
		result.min = Math.min(...selectedData.map(idx => valueRanges[idx].min));
		result.max = Math.max(...selectedData.map(idx => valueRanges[idx].max));
	}
	return result;
};

const mapToScatterTurns = (turns, speedUnit) => {
	const unsortedTurns = turns.map(t => ({
		time: t.twistingTimeS,
		x: speedUnit.formatFine(t.entrySpeedMPerS),
		y: t.angleDegrees,
		date: t.sessionDate,
        title: t.title,
        sessionIsAMatch: t?.sessionIsAMatch
    }));
    return unsortedTurns.sort((a, b) => new Date(a.date) - new Date(b.date)); //sort turns so more recent turns are above
};

export const getFilteredTurns = (
	rawData,
	appliedFilters,
	speedOptions,
	seasonFilter,
	speedUnit
) => {
	// 1) Filter by season
	const turnsBySeason = rawData.filter(turn => seasonFilter(new Date(turn.sessionDate), appliedFilters.seasonSelected));

	// 2) Then filter by the selected date range
	const turnData = getTurnsByDateRange(appliedFilters.range, turnsBySeason);

	// 3) Prepare filter arrays for each metric
	const entrySpeedFilters = speedOptions.map(o => o.turnEntrySpeedFilter);

	const peakAccelerationFilters = [
		t => t.peakAccelerationMPer2 <= 4,
		t => t.peakAccelerationMPer2 > 4 && t.peakAccelerationMPer2 < 5.0,
		t => t.peakAccelerationMPer2 >= 5.0,
	];

	const peakDecelerationFilters = [
		t => t.peakDecelerationMPer2 >= -4.0,
		t => t.peakDecelerationMPer2 < -4.0 && t.peakDecelerationMPer2 > -5.0,
		t => t.peakDecelerationMPer2 <= -5.0,
	];

	const twistingTimeFilters = [
		t => t.twistingTimeS < 0.3,
		t => t.twistingTimeS >= 0.3 && t.twistingTimeS <= 0.5,
		t => t.twistingTimeS > 0.5,
	];

	const angleDegreeFilters = [
		t => t.angleDegrees < 60,
		t => t.angleDegrees >= 60 && t.angleDegrees <= 120,
		t => t.angleDegrees > 120,
	];

	// 4) Filter the data for each metric
	const speedData = filterTurnData(turnData, appliedFilters.speedSelected, entrySpeedFilters);
	const accelerationsData = filterTurnData(turnData, appliedFilters.accelerationsSelected, peakAccelerationFilters);
	const decelerationsData = filterTurnData(turnData, appliedFilters.decelerationsSelected, peakDecelerationFilters);
	const turnTimeData = filterTurnData(turnData, appliedFilters.timesSelected, twistingTimeFilters);
	const angleData = filterTurnData(turnData, appliedFilters.anglesSelected, angleDegreeFilters);

	// 5) Intersect all filtered data to get the "common" set of turns
	const filteredCommonData = speedData.filter(turn =>
        accelerationsData.includes(turn) &&
        decelerationsData.includes(turn) &&
        turnTimeData.includes(turn) &&
        angleData.includes(turn)
	);

	// 6) Calculate max speed & angle to determine chart scaling
	const maxSpeed = calculateMaxTurn(filteredCommonData, "entrySpeedMPerS");
	const paddedMaxSpeed = maxSpeed >= speedUnit.convert(8) ? maxSpeed + speedUnit.convert(0.3) : speedUnit.convert(8);
	const maxAngle = calculateMaxTurn(filteredCommonData, "angleDegrees");

	// 7) Prepare min/max “range” arrays for angle & speed
	const angleValueRanges = [
		{ min: 0, max: 60 },
		{ min: 60, max: 120 },
		{ min: 120, max: maxAngle },
	];

	// Speed range adjustments
	const speedValueRanges = speedOptions.map(o => o.speedValueRange);
	speedValueRanges[speedValueRanges.length - 1].max = paddedMaxSpeed;

	// 8) Get new min/max angle/speed from the user’s selections
	const { min: minAngle, max: maxAngleVal } = getMinMaxPlotValues(appliedFilters.anglesSelected, angleValueRanges, 0, maxAngle);
	const { min: minSpeed, max: maxSpeedVal } = getMinMaxPlotValues(appliedFilters.speedSelected, speedValueRanges, 1, paddedMaxSpeed);

	// 9) Get left & right turns
    const leftTurns = mapToScatterTurns(filteredCommonData.filter(t => t.turnSide === "L"), speedUnit);
    const rightTurns = mapToScatterTurns(filteredCommonData.filter(t => t.turnSide === "R"), speedUnit);

	return {
		filteredCommonData,
		leftTurns,
		rightTurns,
		minAngle,
		maxAngle: maxAngleVal,
		minSpeed,
		maxSpeed: maxSpeedVal,
	};
};